import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getLlamadas } from '../lib/api';
import { CallLog } from '../types';
import {
  Phone,
  RefreshCw,
  Loader,
  AlertCircle,
  Calendar,
  Clock,
  Search,
  ChevronDown,
  X,
  Tag,
} from 'lucide-react';

export const LlamadasHistory: React.FC = () => {
  const [llamadas, setLlamadas] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const porPagina = 10;

  // 🔤 Normalizador de marca: varias grafías → "Xcaret"
  const normalizarXcaret = (texto: unknown): string => {
    if (texto === null || texto === undefined) return '';
    let s = String(texto);
    // Variantes comunes: esh/essh/eshh + caret/carét
    const variantes: RegExp[] = [
      /es{1,2}h{1,2}car[eé]t/gi, // eshcaret, esshcaret, eshhcaret, eshcarét, etc.
      /esh?car[eé]t/gi,          // eshcaret, eshcarét
      /esh+caret/gi,             // eshhcaret
      /esh+car[eé]t/gi,          // eshhcarét
      /es{1,2}h{1,2}\s*car[eé]t/gi, // con espacio accidental: esh caret
    ];
    variantes.forEach(re => { s = s.replace(re, 'Xcaret'); });
    return s;
  };

  // Helpers de formato (consistente con Conversations)
  const formatFecha = (ts: string | number | Date) =>
    new Date(ts).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const formatHora = (ts: string | number | Date) =>
    new Date(ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });

  const roleLabel = (raw?: string) => {
    const r = String(raw || '').toLowerCase();
    if (['assistant', 'asistente', 'ai', 'agent', 'agente', 'bot'].includes(r)) return 'Asistente';
    return 'Usuario';
  };

  const fetchLlamadas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getLlamadas();
      setLlamadas(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.message || 'Error al cargar llamadas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLlamadas();
  }, [fetchLlamadas]);

  const filtradas = useMemo(
    () => (llamadas || []).filter(l => (l?.telefono || '').includes(busqueda)),
    [llamadas, busqueda]
  );

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
  const paginadas = useMemo(
    () => filtradas.slice((pagina - 1) * porPagina, pagina * porPagina),
    [filtradas, pagina]
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchLlamadas();
    } finally {
      setRefreshing(false);
    }
  };

  const toggleExpand = (id: string) =>
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const estadoBadgeClass = (estado?: string) => {
    const e = String(estado || '').toLowerCase();
    if (['completada', 'completed', 'ok', 'success'].includes(e))
      return 'bg-green-900/50 text-green-300 border border-green-700/50';
    if (['fallida', 'failed', 'error', 'no_answer'].includes(e))
      return 'bg-red-900/50 text-red-300 border border-red-700/50';
    if (['en_progreso', 'in_progress', 'ringing', 'ongoing'].includes(e))
      return 'bg-amber-900/50 text-amber-300 border border-amber-700/50';
    return 'bg-gray-700/50 text-gray-300 border border-gray-500/40';
  };

  // Estados
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-[color:var(--acc1)]/90">
        <Loader className="h-10 w-10 animate-spin mb-4" />
        <span>Cargando registros de llamadas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[color:var(--acc2)]/10 border border-[color:var(--acc1)]/20 rounded-lg p-8 text-center">
        <AlertCircle className="h-10 w-10 text-[color:var(--acc1)]/70 mx-auto mb-4" />
        <p className="text-[color:var(--acc1)]/90 text-lg font-medium">No se pudo cargar el historial</p>
        <p className="text-[color:var(--acc1)]/70 mt-2">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Cabecera + Actualizar */}
      <div className="bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] rounded-xl p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Phone className="h-6 w-6 text-[color:var(--acc1)]/90" />
            Historial de Llamadas
          </h2>
          <p className="text-[color:var(--acc1)]/90 text-sm mt-1">
            {filtradas.length} registros encontrados
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="px-4 py-2 bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] hover:opacity-90
                     rounded-lg text-white flex items-center gap-2 transition-all shadow-md hover:shadow-lg
                     disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Actualizar llamadas"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Actualizar</span>
        </button>
      </div>

      {/* Barra de búsqueda (match con Conversations) */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[color:var(--acc1)]/70 h-5 w-5" />
          <input
            type="text"
            placeholder="Buscar por teléfono..."
            className="w-full bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] text-white border border-[color:var(--acc1)]/20 rounded-lg 
                       px-10 py-3 focus:outline-none focus:ring-2 focus:ring-[color:var(--acc1)]/40 focus:border-[color:var(--acc1)]/50
                       placeholder-[color:var(--acc1)]/50 transition-all shadow-inner"
            value={busqueda}
            onChange={e => {
              setBusqueda(e.target.value);
              setPagina(1);
            }}
          />
          {busqueda && (
            <button
              onClick={() => setBusqueda('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[color:var(--acc1)]/70 hover:text-white transition-colors"
              aria-label="Limpiar búsqueda"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Lista / Vacío */}
      {paginadas.length === 0 ? (
        <div className="bg-[color:var(--acc2)]/10 border border-[color:var(--acc1)]/20 rounded-lg p-8 text-center">
          <AlertCircle className="h-10 w-10 text-[color:var(--acc1)]/70 mx-auto mb-4" />
          <p className="text-[color:var(--acc1)]/90 text-lg font-medium">No se encontraron registros</p>
          <p className="text-[color:var(--acc1)]/70 mt-2">Intenta con otro término de búsqueda</p>
        </div>
      ) : (
        <div className="space-y-4">
          {paginadas.map(call => (
            <div
              key={call.id}
              className="bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] border border-[color:var(--acc1)]/20 rounded-lg overflow-hidden
                         transition-all duration-300 hover:shadow-xl hover:border-[color:var(--acc1)]/40"
            >
              {/* Cabecera de tarjeta */}
              <div
                className="p-4 cursor-pointer"
                onClick={() => toggleExpand(call.id)}
              >
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[color:var(--acc2)]/20 rounded-lg flex-shrink-0">
                      <Phone className="h-5 w-5 text-[color:var(--acc1)]/90" />
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-lg">{call.telefono}</h4>
                      <div className="flex items-center text-[color:var(--acc1)]/70 text-xs gap-3 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatFecha(call.fecha_inicio)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatHora(call.fecha_inicio)}
                        </span>
                        <span className="flex items-center gap-1">
                          {`${call.mensaje_count ?? 0} mensajes`}
                          {typeof call.duracion_segundos === 'number' ? ` · ${call.duracion_segundos}s` : ''}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* Badge de estado (simétrico al de sentimiento) */}
                    <span className={`px-3 py-1 text-xs rounded-full font-medium ${estadoBadgeClass(call.estado)}`}>
                      {call.estado || 'desconocido'}
                    </span>

                    {/* Botón de expandir (Chevron con rotación) */}
                    <button
                      className="ml-1 p-1.5 rounded-lg bg-[color:var(--acc2)]/20 hover:bg-[color:var(--acc2)]/30 border border-[color:var(--acc1)]/20
                                 transition-all duration-300"
                      aria-label={expanded[call.id] ? 'Colapsar' : 'Expandir'}
                    >
                      <ChevronDown
                        className={`h-4 w-4 text-[color:var(--acc1)]/90 transition-transform duration-300 ${
                          expanded[call.id] ? 'transform rotate-180' : ''
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Detalle expandido */}
              {expanded[call.id] && (
                <div className="border-t border-[color:var(--acc1)]/20 bg-[color:var(--acc2)]/15 p-4">
                  <div className="space-y-4">
                    {Array.isArray(call.transcript) && call.transcript.length > 0 ? (
                      call.transcript.map((m: any, idx: number) => {
                        // 🔤 normalizamos la marca en texto y keywords
                        const texto = normalizarXcaret(m?.message ?? '');
                        const kws = Array.isArray(m?.keywords)
                          ? m.keywords.map((kw: any) => normalizarXcaret(kw))
                          : [];
                        const rol = roleLabel(m?.role);
                        const isUsuario = rol === 'Usuario';
                        return (
                          <div
                            key={idx}
                            className={`flex flex-col ${isUsuario ? 'items-end' : 'items-start'}`}
                          >
                            {/* Cabecera del mensaje */}
                            <div
                              className={`flex items-center gap-2 text-xs mb-1 ${
                                isUsuario ? 'text-amber-300' : 'text-[color:var(--acc1)]/90'
                              }`}
                            >
                              <div
                                className={`p-1 rounded-md ${
                                  isUsuario ? 'bg-amber-900/40' : 'bg-[color:var(--acc2)]/10'
                                }`}
                              >
                                <span className="inline-block w-2 h-2 rounded-full bg-current" />
                              </div>
                              <span>{rol}</span>
                              <span>•</span>
                              <span>
                                {m?.timestamp ? new Date(m.timestamp).toLocaleString('es-MX') : ''}
                              </span>
                            </div>

                            {/* Burbuja */}
                            <div
                              className={`rounded-lg px-4 py-3 text-sm max-w-xl shadow-md ${
                                isUsuario
                                  ? 'bg-amber-900/20 text-amber-50 border border-amber-800/40'
                                  : 'bg-[color:var(--acc2)]/15 text-white border border-[color:var(--acc1)]/20'
                              }`}
                            >
                              <div className="whitespace-pre-wrap break-words">{texto}</div>

                              {/* Chips de keywords */}
                              {kws.length > 0 && (
                                <div className="mt-3 pt-2 border-t border-[color:var(--acc1)]/15">
                                  <span className="block text-xs text-[color:var(--acc1)]/70 mb-2">
                                    <Tag className="h-3 w-3 inline mr-1" />
                                    Palabras clave
                                  </span>
                                  <div className="flex flex-wrap gap-1.5">
                                    {kws.slice(0, 5).map((kw: any, i: number) => (
                                      <span
                                        key={i}
                                        className="px-2 py-0.5 text-xs rounded-md bg-[color:var(--acc2)]/10
                                                   text-[color:var(--acc1)]/90 border border-[color:var(--acc1)]/20 hover:bg-[color:var(--acc2)]/15"
                                      >
                                        {String(kw)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-[color:var(--acc1)]/90 text-sm">Sin transcripción disponible.</div>
                    )}

                    {call.audio_url && (
                      <audio controls className="w-full mt-2">
                        <source src={call.audio_url} type="audio/mpeg" />
                        Tu navegador no soporta audio embebido.
                      </audio>
                    )}

                    <div className="text-[color:var(--acc1)]/70 text-xs">Estado: {call.estado}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Paginación (match con Conversations) */}
      {totalPaginas > 1 && (
        <div className="flex flex-wrap justify-center items-center gap-2 mt-8">
          <div className="bg-[linear-gradient(135deg,var(--acc1),var(--acc2))] rounded-lg p-1.5 flex items-center border border-[color:var(--acc1)]/20">
            <button
              disabled={pagina === 1}
              onClick={() => setPagina(1)}
              className="px-2 py-1 rounded-md bg-transparent hover:bg-[color:var(--acc2)]/20 
                         disabled:opacity-40 text-[color:var(--acc1)]/90 text-sm disabled:hover:bg-transparent
                         disabled:cursor-not-allowed transition-colors"
              aria-label="Primera página"
            >
              Primera
            </button>

            <button
              disabled={pagina === 1}
              onClick={() => setPagina(p => Math.max(1, p - 1))}
              className="px-3 py-1 rounded-md mx-1 bg-transparent hover:bg-[color:var(--acc2)]/20
                         disabled:opacity-40 text-[color:var(--acc1)]/85 disabled:hover:bg-transparent
                         disabled:cursor-not-allowed transition-colors"
              aria-label="Página anterior"
            >
              Anterior
            </button>

            <div className="px-3 py-1 rounded-md bg-[color:var(--acc2)]/20 text-white mx-1">
              <span className="font-medium">{pagina}</span>
              <span className="text-[color:var(--acc1)]/70 mx-1">de</span>
              <span className="font-medium">{totalPaginas}</span>
            </div>

            <button
              disabled={pagina === totalPaginas}
              onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
              className="px-3 py-1 rounded-md mx-1 bg-transparent hover:bg-[color:var(--acc2)]/20
                         disabled:opacity-40 text-[color:var(--acc1)]/85 disabled:hover:bg-transparent
                         disabled:cursor-not-allowed transition-colors"
              aria-label="Página siguiente"
            >
              Siguiente
            </button>

            <button
              disabled={pagina === totalPaginas}
              onClick={() => setPagina(totalPaginas)}
              className="px-2 py-1 rounded-md bg-transparent hover:bg-[color:var(--acc2)]/20 
                         disabled:opacity-40 text-[color:var(--acc1)]/90 text-sm disabled:hover:bg-transparent
                         disabled:cursor-not-allowed transition-colors"
              aria-label="Última página"
            >
              Última
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
